import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";

import { SortParams } from "@/types/types";

import { AssignStatus } from "@/enum/list.enum";

import {
	renderAssignToColumn,
	renderConnectionsColumn,
	renderEstimatedNumEmployeesColumn,
	renderGenderColumn,
	renderGoogleMapsAverageRatingColumn,
	renderGoogleMapsColumn,
	renderGoogleMapsCompanyCityColumn,
	renderGoogleMapsCompanyCountryColumn,
	renderGoogleMapsCompanyStateColumn,
	renderGoogleMapsReviewAmountColumn,
	renderGoogleMapsTypeColumn,
	renderGoogleMapsWebsiteColumn,
	renderIndustryColumn,
	renderLanguageColumn,
	renderMapCompanyColumn,
	renderNameColumn,
	renderPersonCityColumn,
	renderPersonCountryColumn,
	renderPersonStateColumn,
	renderSelectColumn,
	renderTitleColumn,
	renderSeniorityColumn,
	renderMapNameColumn,
	renderFollowers,
	renderSourceNameColumn,
	renderCompanyPeopleCheckColumn,
	renderAboutColumn,
	renderHeadlineColumn,
	renderCreatedAt,
	renderSalutationDeColumn,
	renderSalutationEnColumn,
	renderMapSalutationDeColumn,
	renderMapSalutationEnColumn,
	renderExclusionReasonColumn,
	renderGoogleMapsFacebookColumn,
	renderGoogleMapsYelpColumn,
	renderGoogleMapsTiktokColumn,
	renderGoogleMapsInstagramColumn,
	renderGoogleMapsSnapchatColumn,
	renderGoogleMapsTwitterColumn,
	renderGoogleMapsYoutubeColumn,
	renderGoogleMapsGithubColumn,
	renderGoogleMapsPinterestColumn,
	renderPreviousEmployersColumn,
	renderCompanyScoreColumn,
	renderPersonaScoreColumn,
	renderCompanyCategoryColumn,
	renderPersonaCategoryColumn,
	renderReportetEmployeesColumn,
	renderRoleChangeColumn,
	renderCompanyColumn,
	renderPreviousJobTitleColumn,
	renderPreviousEmployerColumn,
	renderCampaignAddedAt,
	renderAssignedStatusColumn,
	renderLastNodeTypeColumn,
	renderLastScheduledAtColumn,
	renderLastMessageColumn,
	renderDeepResearchStatusColumn,
} from "../../utils/allColumns";
import {
	renderCompanyOtherListsColumn,
	renderOtherListsColumn,
} from "../../utils/otherListsColumns";
import { renderMaybeColumn } from "../../utils/renderMaybeColumn";
import { GetIrlColumns } from "../CustomerImplementations/IrlColumns";

export interface GoogleMapsTableColumnsProps {
	selectAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	parts: string[];
	sortingInbox: SortParams;
	setShowEdit: ({
		id,
		name,
		columnConfigId,
	}: {
		id: number;
		name: string;
		columnConfigId?: number;
	}) => void;
	showEdit: { id: number; name: string; columnConfigId?: number } | undefined;
	integrations: { [key: string]: boolean | undefined };
	handleSelectCampaignAndProspect: (
		id: number | string,
		listPersonId: number,
		type?: "internal" | "lemlist"
	) => void;
	assignStatus: AssignStatus;
	onResearchClick?: (row: import("../../schema/list").ListPerson) => void;
	onOpenDetail?: (row: import("../../schema/list").ListPerson) => void;
}

export const GoogleMapsTableColumns = ({
	selectAll,
	setShowAll,
	toggleSelectAll,
	selectedItems,
	handleSelectItem,
	handleCopyClick,
	ts,
	parts,
	sortingInbox,
	setShowEdit,
	showEdit,
	integrations,
	handleSelectCampaignAndProspect,
	assignStatus,
	onResearchClick,
	onOpenDetail,
}: GoogleMapsTableColumnsProps): TableColumn[] => {
	const columns: TableColumn[] = [
		renderSelectColumn(setShowAll, toggleSelectAll, selectAll, selectedItems, handleSelectItem),
		renderMaybeColumn(ts),
		renderAssignToColumn(ts, handleCopyClick, handleSelectCampaignAndProspect),
	];

	if (parts.includes("prospect")) {
		columns.push(renderNameColumn(ts, handleCopyClick, sortingInbox, onOpenDetail));
		columns.push(renderCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit));
		columns.push(renderExclusionReasonColumn(ts, sortingInbox));
		columns.push(renderSalutationDeColumn(ts));
		columns.push(renderSalutationEnColumn(ts));
	} else if (parts.includes("managingDirector")) {
		columns.push(renderMapNameColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit));
		columns.push(renderMapCompanyColumn(ts, handleCopyClick, sortingInbox, "leftFifth"));
		columns.push(renderExclusionReasonColumn(ts, sortingInbox));
		columns.push(renderMapSalutationDeColumn(ts));
		columns.push(renderMapSalutationEnColumn(ts));
	} else {
		columns.push(renderMapCompanyColumn(ts, handleCopyClick, sortingInbox));
	}

	if (parts.includes("prospect")) {
		columns.push(renderGenderColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit));
		columns.push(renderTitleColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderHeadlineColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderAboutColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderSeniorityColumn(ts, handleCopyClick, sortingInbox));
	}

	if (parts.includes("prospect")) {
		columns.push(renderCompanyPeopleCheckColumn(ts));
		columns.push(renderCompanyOtherListsColumn(ts, sortingInbox));
	}

	columns.push(renderDeepResearchStatusColumn(onResearchClick));
	columns.push(renderGoogleMapsColumn(ts, sortingInbox));
	columns.push(renderGoogleMapsReviewAmountColumn(ts, sortingInbox));
	columns.push(renderGoogleMapsAverageRatingColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderGoogleMapsTypeColumn(ts, sortingInbox));
	columns.push(renderGoogleMapsPinterestColumn(ts));
	columns.push(renderGoogleMapsFacebookColumn(ts));
	columns.push(renderGoogleMapsYelpColumn(ts));
	columns.push(renderGoogleMapsTiktokColumn(ts));
	columns.push(renderGoogleMapsInstagramColumn(ts));
	columns.push(renderGoogleMapsSnapchatColumn(ts));
	columns.push(renderGoogleMapsTwitterColumn(ts));
	columns.push(renderGoogleMapsYoutubeColumn(ts));
	columns.push(renderGoogleMapsGithubColumn(ts));
	columns.push(renderGoogleMapsWebsiteColumn(ts, sortingInbox));
	columns.push(renderGoogleMapsCompanyCountryColumn(ts, sortingInbox));
	columns.push(renderGoogleMapsCompanyStateColumn(ts, sortingInbox));
	columns.push(renderGoogleMapsCompanyCityColumn(ts, sortingInbox));

	if (parts?.includes("prospect")) {
		columns.push(renderLanguageColumn(ts, sortingInbox));
		columns.push(renderPersonCityColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderPersonStateColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderPersonCountryColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderEstimatedNumEmployeesColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderReportetEmployeesColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderIndustryColumn(ts, handleCopyClick, sortingInbox));
		columns.push(renderConnectionsColumn(ts, sortingInbox));
		columns.push(renderPreviousEmployersColumn(ts));
		columns.push(renderPreviousEmployerColumn(ts));
		columns.push(renderPreviousJobTitleColumn(ts));
		columns.push(renderFollowers(ts, sortingInbox));

		if (integrations.IRL_COUNTRIES_SUM) {
			const irlColumns = GetIrlColumns(ts);

			columns.push(...irlColumns);
		}
	}

	if (assignStatus === AssignStatus.ASSIGNED) {
		columns.push(renderCampaignAddedAt(ts, sortingInbox));
		columns.push(renderAssignedStatusColumn(ts, sortingInbox));
		columns.push(renderLastNodeTypeColumn(ts, sortingInbox));
		columns.push(renderLastScheduledAtColumn(ts, sortingInbox));
		columns.push(renderLastMessageColumn(ts));
	}

	if (integrations.LEAD_SCORING) {
		columns.push(renderPersonaCategoryColumn(sortingInbox));
		columns.push(renderCompanyCategoryColumn(sortingInbox));
		columns.push(renderPersonaScoreColumn(sortingInbox));
		columns.push(renderCompanyScoreColumn(sortingInbox));
	}

	columns.push(renderOtherListsColumn(ts, sortingInbox));
	columns.push(renderSourceNameColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderCreatedAt(ts, sortingInbox));

	columns.push(renderRoleChangeColumn());

	return columns;
};
